/**
 * La présence du fan — ce que Kayla a validé le 29-10-2026 (PR #1333 à #1335 du cockpit), en
 * pièces de librairie :
 *
 *   · `PointEnLigne` : le point vert qui PULSE (une onde derrière, coupée sous « mouvement réduit ») ;
 *   · `PastilleDePresence` : collée au nom dans l'en-tête — le `Badge` vert « En ligne », gris
 *     « Vu il y a 5 minutes » (« pas assez visible, noyé dans pas mal d'infos ») ;
 *   · `AvatarDeConversation` : l'`Avatar` de la librairie et ses deux coins — en haut la présence
 *     (le point, ou la durée d'absence « 3 min », « 2 h », « 4 j » : « si le mec est inactif depuis
 *     1 minute ou 3 minutes, il n'y a rien — on ne sait pas depuis combien de temps »), en bas le
 *     réseau.
 *
 * Les mots arrivent déjà écrits (« En ligne », « Vu il y a 5 minutes », « 3 min ») : la durée se
 * calcule dans l'app, à sa langue. Pas de relevé = rien, jamais un « 0 ».
 *
 *   <AvatarDeConversation src={fan.photo} graine={fan.id} enLigne libellePresence="En ligne" reseau={<LogoTelegram />} />
 *   <PastilleDePresence etat="parti" libelle="Vu il y a 5 minutes" />
 */
import type { CSSProperties, ReactNode } from "react";

import { Avatar, type AvatarProps } from "../Avatar";
import { Badge } from "../Badge";
import { cn } from "../utils";
import "./conversation.css";

export interface PointEnLigneProps {
  /** Dit au lecteur d'écran et au survol. Défaut « En ligne ». */
  libelle?: string;
  /** Côté en px. Défaut 12. */
  taille?: number;
  className?: string;
}

export function PointEnLigne({ libelle = "En ligne", taille = 12, className }: PointEnLigneProps) {
  return (
    <span
      className={cn("cv-point", className)}
      role="img"
      aria-label={libelle}
      title={libelle}
      style={{ "--cv-point": `${taille}px` } as CSSProperties}
    >
      <span className="cv-point-onde" aria-hidden />
      <span className="cv-point-coeur" aria-hidden />
    </span>
  );
}

export type EtatDePresence = "en_ligne" | "parti";

export interface PastilleDePresenceProps {
  etat: EtatDePresence;
  /** « En ligne », « Vu il y a 5 minutes », « Vu récemment » — déjà traduit. */
  libelle: string;
  className?: string;
}

export function PastilleDePresence({ etat, libelle, className }: PastilleDePresenceProps) {
  return (
    <Badge ton={etat === "en_ligne" ? "vert" : "gris"} point size="md" className={className}>
      {libelle}
    </Badge>
  );
}

/** Qui est sur l'avatar : la photo, ou à défaut une graine stable (l'id du fan), ou le nom. */
export type IdentiteAvatar = Pick<AvatarProps, "src" | "nom" | "graine" | "icone" | "alt">;

export interface AvatarDeConversationProps extends IdentiteAvatar {
  /** Côté en px. Défaut 44. */
  taille?: number;
  /** Le point vert qui pulse, dans le coin haut. */
  enLigne?: boolean;
  /** Parti, heure connue : la durée courte (« 3 min »), dans le coin haut. Ignoré si `enLigne`. */
  partiDepuis?: string | null;
  /** Le libellé entier, pour le survol et le lecteur d'écran (« En ligne », « Vu il y a 3 minutes »). */
  libellePresence?: string;
  /** Dans le coin bas : le logo du réseau (Telegram, X). */
  reseau?: ReactNode;
  /** Côté du point vert, en px. Défaut 14. */
  taillePoint?: number;
  className?: string;
}

export function AvatarDeConversation({
  taille = 44,
  enLigne = false,
  partiDepuis,
  libellePresence,
  reseau,
  taillePoint = 14,
  className,
  ...identite
}: AvatarDeConversationProps) {
  // l'Avatar est une image : ses coins n'y sont pas lus — la présence rejoint son nom
  const presence = enLigne || partiDepuis ? (libellePresence ?? partiDepuis ?? undefined) : undefined;
  const nom = identite.alt ?? identite.nom;
  const alt = nom && presence ? `${nom}, ${presence}` : nom;
  return (
    <Avatar {...identite} alt={alt} taille={taille} className={cn("cv-avatar-coins", className)}>
      {enLigne ? (
        <span className="cv-coin-haut">
          <PointEnLigne libelle={libellePresence} taille={taillePoint} />
        </span>
      ) : partiDepuis ? (
        <span className="cv-coin-haut">
          <span className="cv-parti" role="img" aria-label={libellePresence ?? partiDepuis} title={libellePresence ?? partiDepuis}>
            <Badge ton="gris">{partiDepuis}</Badge>
          </span>
        </span>
      ) : null}
      {reseau !== undefined && reseau !== null && <span className="cv-coin-bas">{reseau}</span>}
    </Avatar>
  );
}
